import { format } from "date-fns";

export const TIME_DEFAULTS = {
  START_OF_DAY: { h: 0, m: 0, s: 0 },
  END_OF_DAY: { h: 23, m: 59, s: 59 },
} as const;

/**
 * Returns a new Date set to the specified time of day on the given date
 * (or today if no date is provided).
 */
function atTimeOfDay(
  d: Date | undefined,
  { h, m, s }: { h: number; m: number; s: number },
): Date {
  const base = d ? new Date(d) : new Date();
  base.setHours(h, m, s, 0);
  return base;
}

/**
 * Returns a Date set to the start of the given date
 * (or today if no date is provided).  The time is based on
 * TIME_DEFAULTS: (00:00:00)
 * Use to default TimeSlotStart dates
 */
export function getStartOfDay(d?: Date): Date {
  return atTimeOfDay(d, TIME_DEFAULTS.START_OF_DAY);
}

/**
 * Returns a Date set to the end of the given date
 * (or today if not date is provided).  The time is based on
 * TIME_DEFAULTS: (23:59:59)
 * Use to default TimeSlotEnd dates
 */
export function getEndOfDay(d?: Date): Date {
  return atTimeOfDay(d, TIME_DEFAULTS.END_OF_DAY);
}

/**
 * Converts a backend time string/Date/null to a Date with that
 * time-of-day.
 */
export function toValidTime(value?: string | Date | null): Date | null {
  const seconds = normalizeToTimeOfDaySeconds(value);
  if (seconds == null) return null;

  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setSeconds(seconds);
  return date;
}

export function parseTimeOrStartOfDay(value?: string | null): Date {
  return toValidTime(value) ?? getStartOfDay();
}

export function parseTimeOrEndOfDay(value?: string | null): Date {
  return toValidTime(value) ?? getEndOfDay();
}

/**
 * Parses a date string into a Date object.
 * Returns null for null, undefined, empty, or invalid values.
 * Intended for API/database input to prevent an invalid date
 * from entering editor.
 */
export function toValidDate(value?: string | null): Date | null {
  if (!value) return null;

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Normalizes a date value to its local calendar date (YYYY-MM-DD), ignoring time.
 */
export function normalizeToDateOnly(
  value: string | Date | null | undefined,
): string | null {
  if (!value) return null;

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  return format(date, "yyyy-MM-dd");
}

/**
 * Compares two calendar dates for equality, ignoring time and timezone.
 *
 * Intended for diffing persisted rules (strings) against editor state (Date objects).
 * Both values are normalized to a date-only representation before comparison.
 *
 * - Time-of-day differences are ignored
 * - Invalid or missing values are treated as `null`
 *
 * @param original The persisted date value from the API/database (string or null).
 * @param current The current editor value (Date or null).
 * @returns true if both values represent the same calendar date, otherwise false.
 */
export function areDatesEqual(
  original: string | null | undefined,
  current: Date | null | undefined,
): boolean {
  return normalizeToDateOnly(original) === normalizeToDateOnly(current);
}

/**
 * Normalizes a backend time value to seconds since midnight.
 * Accepts Date or "HH:mm[:ss]" strings. Returns null if invalid.
 */
export function normalizeToTimeOfDaySeconds(
  value: string | Date | null | undefined,
): number | null {
  if (!value) return null;

  if (value instanceof Date) {
    return (
      value.getHours() * 3600 + value.getMinutes() * 60 + value.getSeconds()
    );
  }

  const [h, m, s = 0] = value.split(":").map(Number);
  if (
    [h, m, s].some(Number.isNaN) ||
    h < 0 ||
    h > 23 ||
    m < 0 ||
    m > 59 ||
    s < 0 ||
    s > 59
  ) {
    return null;
  }

  return h * 3600 + m * 60 + s;
}

/**
 * Compares two time-of-day values for equality, ignoring any date component.
 *
 * Intended for comparing backend/API time values (strings)
 * with editor values (Date). Normalizes both inputs to
 * seconds since midnight before comparison.
 *
 * - Dates are compared by hours/minutes/seconds only
 * - Invalid or missing values are treated as `null`
 *
 * @param original The time value from the API/database (string or null).
 * @param current The current editor value (Date or null).
 * @returns true if both values represent the same time of day, otherwise false.
 */
export function areTimesEqual(
  original: string | null | undefined,
  current: Date | null | undefined,
): boolean {
  return (
    normalizeToTimeOfDaySeconds(original) ===
    normalizeToTimeOfDaySeconds(current)
  );
}
